import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import { changesSinceReturn } from '../components/workflow/changeDiff';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import SplitList from '../components/workflow/SplitList';
import CompanyList from '../components/workflow/CompanyList';
import AttachmentsList from '../components/workflow/AttachmentsList';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider } from '../components/workflow/fieldComments';
import {
  bizFileOf, BIZFILE_DOC_TYPE, SCREENING_DOC_TYPE, SCREENING_SECTION_HINT,
} from './WorkflowEngRegNew';
import { ProfileDetailItem } from '../components/workflow/ProfileDetail';
import { PROFILE_CHANGE_LABELS, pieText, profileForEntity } from '../utils/engagementProfile';
import { serviceSubtitle } from '../utils/serviceLines';
import { docTypeBadge, detailLabel, splitListReadValue } from '../components/workflow/constants';
import { bizFileRequired } from '../components/shared/entityCategory';
import WorkflowStatusBadgeChip from '../components/workflow/WorkflowStatusBadge';
import { ScreeningAnswersView, SCREENING_LABELS } from '../components/shared/ScreeningFields';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import NowName, { StaffNames } from '../components/shared/NowName';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Engagement Registration — detail page (docs/modules/workflow-engreg.md).
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

// The field names this request is read by — History, the changes-since-return
// table, the per-field return comments and the edit page all name a field the
// same way because they read this one map.
export const CHANGE_LABELS = {
  clientGroup: 'Client Group',
  yearEnd: 'Financial Year End',
  serviceProvided: 'Service Provided',
  serviceOther: 'Other Service',
  descriptionOfService: 'Description of Service',
  ultimateParent: 'Ultimate Parent',
  overseasOperations: 'Overseas Operations',
  consolidatedFs: 'Consolidated FS',
  isPie: 'Public Interest Entity (PIE)?',
  pieType: 'Type of PIE',
  pieTypeOther: 'Type of PIE (if Others)',
  // The per-company rows of every profile group (<group>Split).
  ...PROFILE_CHANGE_LABELS,
  proposedFees: 'Estimated Fees',
  proposedFeesCcy: 'Estimated Fees Currency',
  proposedFeesSplit: 'Estimated Fees per Engagement',
  serviceLines: 'Additional Services',
  ssicPrimary: 'SSIC Code Primary',
  ssicSecondary: 'SSIC Code Secondary',
  principalActivity1: 'Principal Activity 1',
  principalActivity2: 'Principal Activity 2',
  secRegistered: 'SEC Registered',
  dateOfAppointment: 'Date of Appointment',
  isGroupAudit: 'Group Audit',
  underInvestigation: 'Under Investigation',
  tcwgFeeCommunicated: 'Fee Information Communicated to TCWG',
  screeningRequired: 'QM Screening',
  ultimateParentHqCity: SCREENING_LABELS.ultimateParentHqCity,
  ubo: SCREENING_LABELS.ubo,
  isTne: 'Transnational Entity (TNE)',
  tneBasis: SCREENING_LABELS.tneBasis,
  isTna: 'Transnational Audit (TNA)',
  tnaBasis: SCREENING_LABELS.tnaBasis,
  remarks: 'Remarks',
  entityNames: 'Companies',
  partnerNames: 'Partner in Charge',
  directorNames: 'Director(s)',
  managerNames: 'Manager(s)',
  teamMembers: 'Team Member(s)',
};

// A BizFile belongs to ONE company, so the badge says which —
// on a group registration three PDFs otherwise look interchangeable.
const bizFileBadge = (file, entities = []) => {
  if (file.docType !== BIZFILE_DOC_TYPE) return docTypeBadge(file.docType);
  const owner = (entities || []).find((e) => e.entityId === file.entityId);
  const [label, cls] = docTypeBadge(BIZFILE_DOC_TYPE);
  return [owner ? `${label} · ${owner.entityName}` : label, cls];
};

// The three attachment kinds never share a list: BizFile is the mandatory
// per-company document, Screening is the one QM-related PDF, and everything
// else is supporting material.
const bizFilesOf = (request) => (request.files || []).filter((fl) => fl.docType === BIZFILE_DOC_TYPE);
const screeningFilesOf = (request) => (request.files || []).filter((fl) => fl.docType === SCREENING_DOC_TYPE);
const isSupporting = (fl) => fl.docType !== BIZFILE_DOC_TYPE && fl.docType !== SCREENING_DOC_TYPE;
const supportingFilesOf = (request) => (request.files || []).filter(isSupporting);

// Legacy requests (filed before the BizFile became mandatory) and companies
// added later — named so the reviewer knows what is still missing. Only a
// Singapore Company owes one; a Government Agency / Overseas Company does not.
const missingBizFileNames = (request) => (request.entities || [])
  .filter((e) => bizFileRequired(e.entityCategory) && !bizFileOf(request.files, e.entityId))
  .map((e) => e.entityName);

const WorkflowEngRegDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [request, setRequest] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [review, setReview] = useState(null);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(location.state?.notice || null);
  const [rejecting, setRejecting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [acting, setActing] = useState(false);
  // The Screening request this approval opened (Yes answers only).
  const [screeningRequest, setScreeningRequest] = useState(null);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/engreg/${id}`),
        fetchJSON(`/api/workflow/engreg/${id}/events`),
      ]);
      setRequest(detail.request);
      setCanReview(detail.canReview);
      setReview(detail.review || null);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setScreeningRequest(detail.screeningRequest || null);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true); setError(null);
    try {
      const data = await fetchJSON(`/api/workflow/engreg/${id}/approve`, { method: 'POST' });
      setNotice(data.screeningRequest
        ? `Approved — the engagements have been created and Screening request ${data.screeningRequest.requestNo} has been opened for QM.`
        : 'Approved — the engagements have been created (Pending Acceptance). File the QC6 / QC7 form next.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';
  // Saved but not sent back. Re-submitting lives on the edit page now, so a
  // request can sit here with the reviewer's changes already made and nobody
  // waiting on them — this row is what says so.
  const savedNotSent = hasAwaitingInfo && !!changesSinceReturn(events)
    && (events.find((ev) => ev.action === 'modified') || null);
  const service = request && (request.serviceProvided === 'Other'
    ? `Other — ${request.serviceOther || ''}` : request?.serviceProvided);
  const hasFee = (x) => x.proposedFees !== null && x.proposedFees !== undefined;
  const feesSplit = !!request && (request.entities || []).length + (request.serviceLines || []).length > 1
    && ((request.entities || []).some(hasFee) || (request.serviceLines || []).some(hasFee));
  // The engagements the approval will create, in creation order: each
  // company's own service first, then its additional service lines. Two or
  // more of them and the Project card lists them instead of printing the
  // first one's service, description and fee as if they were the request's.
  const engagementRows = !request ? [] : [
    ...(request.entities || []).map((e) => ({
      key: `e${e.id}`,
      entityName: e.entityName,
      entityNameNow: e.entityNameNow,
      subtitle: serviceSubtitle(profileForEntity(request, e)),
      proposedFees: e.proposedFees,
      engagementId: e.engagementId,
      additional: false,
    })),
    ...(request.serviceLines || []).map((l) => ({
      key: `l${l.id}`,
      entityName: l.entityName,
      entityNameNow: l.entityNameNow,
      subtitle: serviceSubtitle(l),
      proposedFees: l.proposedFees,
      engagementId: l.engagementId,
      additional: true,
    })),
  ];
  const multiEngagements = engagementRows.length > 1;

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (!!review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/engreg"
      backLabel="Engagement Registration"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Engagement Registration"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && request.status === 'requested' ? approve : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => navigate(`/workflow/engreg/${id}/edit`) : null}
            modifyLabel={hasAwaitingInfo ? 'Modify & Re-submit' : 'Modify'}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            {savedNotSent && (
              <div>
                <p className={detailLabel}>Unsent Changes</p>
                <p className="text-sm text-amber-700 mt-1">
                  Saved {formatDateTime(savedNotSent.createdAt)} — not re-submitted yet
                </p>
              </div>
            )}
            {request.status === 'approved' && (
              <div>
                <p className={detailLabel}>Next Step</p>
                <p className="text-sm text-green-600 mt-1">
                  Engagements created — <Link to="/workflow/qc/new" className="underline">file the QC6 / QC7 form</Link>
                </p>
              </div>
            )}
            {screeningRequest && (
              <div>
                <p className={detailLabel}>Screening</p>
                <p className="text-sm text-slate-700 mt-1 inline-flex items-center gap-2">
                  <RefLink to={`/workflow/screening/${screeningRequest.id}`} className="text-blue-600 underline">{screeningRequest.requestNo}</RefLink>
                  <WorkflowStatusBadgeChip status={screeningRequest.status} />
                </p>
              </div>
            )}
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title="Project">
            <DetailGrid>
              <DetailItem label="Client Group" field="clientGroup"><NowName value={request.clientGroup} now={request.clientGroupNow} /></DetailItem>
              <DetailItem label="Financial Year End" field="yearEnd">{formatDate(request.yearEnd)}</DetailItem>
              <DetailItem label={`Companies (${(request.entities || []).length})`} field="entityNames">
                <CompanyList
                  entities={request.entities || []}
                  controlOf={(e) => (e.engagementId ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700">
                      engagement created
                    </span>
                  ) : null)}
                />
              </DetailItem>
              {/* One engagement — the service and its description read as two
                  plain answers. Two or more (several companies, or service
                  lines): the engagements list below says it per engagement,
                  and these two answers would only print the first one. */}
              {!multiEngagements && (
                <>
                  <ProfileDetailItem request={request} groupKey="service" label="Service Provided" field="serviceProvided">{service}</ProfileDetailItem>
                  <ProfileDetailItem request={request} groupKey="descriptionOfService" label="Description of Service" field="descriptionOfService" />
                </>
              )}
              {/* Legacy requests only — the date now lives on the client master
                  (Clients & Entities) and is not asked here. */}
              {request.dateOfAppointment && (
                <DetailItem label="Date of Appointment" field="dateOfAppointment">{formatDate(request.dateOfAppointment)}</DetailItem>
              )}
              {/* Total only (no company has its own amount) reads as one plain
                  Estimated Fees; split amounts sit on the engagements list. */}
              <DetailItem label={feesSplit ? 'Estimated Fees — Total' : 'Estimated Fees'} field="proposedFees">{request.proposedFees != null ? `${request.proposedFeesCcy || 'SGD'} ${request.proposedFees.toLocaleString()}` : '—'}</DetailItem>
              {multiEngagements && (
                <div className="sm:col-span-2">
                  <DetailItem label={`Engagements (${engagementRows.length})`} field="serviceLines">
                    <SplitList
                      controlClass={splitListReadValue}
                      rows={engagementRows.map((r) => ({
                        key: r.key,
                        title: (
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <NowName value={r.entityName} now={r.entityNameNow} />
                            {r.additional && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                                additional
                              </span>
                            )}
                            {r.engagementId && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700">
                                engagement created
                              </span>
                            )}
                          </span>
                        ),
                        subtitle: r.subtitle,
                        control: feesSplit && r.proposedFees != null
                          ? `${request.proposedFeesCcy || 'SGD'} ${Number(r.proposedFees).toLocaleString()}` : '—',
                      }))}
                    />
                    <p className="text-xs text-slate-500 mt-0.5">A separate engagement is created for each line on approval.</p>
                    {/* The three answers this list stands in for keep their own
                        comment entries — the list is one block, a DetailItem
                        carries one field. One row, so three hidden buttons do
                        not leave three empty lines under the list. */}
                    <div className="flex flex-wrap items-start gap-x-4">
                      <FieldCommentAnchor field="serviceProvided" label="Service Provided" compact hoverOnly className="mt-1" />
                      <FieldCommentAnchor field="descriptionOfService" label="Description of Service" compact hoverOnly className="mt-1" />
                      <FieldCommentAnchor field="proposedFeesSplit" label="Estimated Fees per Engagement" compact hoverOnly className="mt-1" />
                    </div>
                  </DetailItem>
                </div>
              )}
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Client Profile">
            <DetailGrid>
              {/* Each answer reads per company when the companies answered it on their own. */}
              <DetailItem label="Ultimate Parent" field="ultimateParent">{request.ultimateParent || '—'}</DetailItem>
              <ProfileDetailItem request={request} groupKey="overseasOperations" label="Overseas Operations / Overseas Subsidiary" field="overseasOperations" />
              <ProfileDetailItem request={request} groupKey="consolidatedFs" label="Consolidated Financial Statements" field="consolidatedFs" />
              <ProfileDetailItem request={request} groupKey="pie" label="Public Interest Entity (PIE)" field="isPie">{pieText(request) || '—'}</ProfileDetailItem>
              <ProfileDetailItem request={request} groupKey="ssicPrimary" label="SSIC Code Primary" field="ssicPrimary" />
              <ProfileDetailItem request={request} groupKey="ssicSecondary" label="SSIC Code Secondary" field="ssicSecondary" />
              <ProfileDetailItem request={request} groupKey="principalActivity1" label="Principal Activity 1" field="principalActivity1" />
              <ProfileDetailItem request={request} groupKey="principalActivity2" label="Principal Activity 2" field="principalActivity2" />
              <ProfileDetailItem request={request} groupKey="secRegistered" label="Registered with the SEC (US)" field="secRegistered" />
              <ProfileDetailItem request={request} groupKey="isGroupAudit" label="Group Audit" field="isGroupAudit" />
              <ProfileDetailItem request={request} groupKey="underInvestigation" label="Under Investigation by Regulatory Authorities" field="underInvestigation" />
              <ProfileDetailItem request={request} groupKey="tcwgFeeCommunicated" label="Fee Information Communicated to TCWG" field="tcwgFeeCommunicated" />
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="People">
            <DetailGrid>
              <DetailItem label="Partner in Charge" field="partnerNames"><StaffNames list={request.partners} /></DetailItem>
              <DetailItem label="Director(s)" field="directorNames"><StaffNames list={request.directors} /></DetailItem>
              <DetailItem label="Manager(s)" field="managerNames"><StaffNames list={request.managers} /></DetailItem>
              <DetailItem label="Team Member(s)" field="teamMembers"><StaffNames list={request.teamMembers} /></DetailItem>
            </DetailGrid>
          </SectionCard>

          {/* Screening — its own card, ABOVE BizFile, and it
              carries its own question: Yes shows the six screening answers QM
              will work from (approval opens a Screening request); No shows the
              requester's own document, if any. */}
          <SectionCard title="Screening">
            <p className="text-xs text-slate-500 mb-2">{SCREENING_SECTION_HINT}</p>
            <DetailGrid>
              <DetailItem label="Does QM need to perform screening?" field="screeningRequired">{yesNo(request.screeningRequired)}</DetailItem>
            </DetailGrid>
            {request.screeningRequired === true && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <ScreeningAnswersView r={request} />
                <p className="text-sm text-slate-500 mt-2">
                  {screeningRequest
                    ? <>QM performs the screening in <RefLink to={`/workflow/screening/${screeningRequest.id}`} className="text-blue-600 underline">{screeningRequest.requestNo}</RefLink>.</>
                    : 'QM performs the screening in a Screening request opened when this registration is approved.'}
                </p>
              </div>
            )}
            {request.screeningRequired !== true && (
              <div className="mt-3">
                <AttachmentsList
                  files={screeningFilesOf(request)}
                  downloadHref={(fl) => `${API_BASE_URL}/api/workflow/engreg/${request.id}/files/${fl.id}`}
                  emptyText="No screening document attached."
                />
              </div>
            )}
          </SectionCard>

          {/* Two separate cards, not one list: the BizFile
              is a mandatory named document per company; Attachments is the
              free-form supporting material. */}
          <SectionCard title="BizFile">
            <AttachmentsList
              files={bizFilesOf(request)}
              badgeFor={(fl) => bizFileBadge(fl, request.entities)}
              downloadHref={(fl) => `${API_BASE_URL}/api/workflow/engreg/${request.id}/files/${fl.id}`}
              emptyText="No BizFile attached."
            />
            <FieldCommentAnchor field="files" label="Attachments" />
            {/* An approved registration is locked, so telling the reviewer to
                press Modify would be pointing at a button that is not there —
                those are legacy requests filed before the BizFile was
                mandatory, and they stay as filed. */}
            {missingBizFileNames(request).length > 0 && (
              request.status === 'approved' ? (
                <p className="text-sm text-slate-500 mt-2">
                  Filed before the BizFile became mandatory — an approved registration stays as it was
                  approved.
                </p>
              ) : (
                <p className="text-sm text-amber-700 mt-2">
                  No BizFile attached for: {missingBizFileNames(request).join(', ')} — the requester must add it
                  (Modify) before this registration can be approved.
                </p>
              )
            )}
          </SectionCard>

          <SectionCard title="Attachments">
            <AttachmentsList
              files={supportingFilesOf(request)}
              downloadHref={(fl) => `${API_BASE_URL}/api/workflow/engreg/${request.id}/files/${fl.id}`}
              emptyText="No supporting files attached."
            />
          </SectionCard>

          <HistoryCard events={events} changeLabels={CHANGE_LABELS} />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/engreg/${request.id}/reject`, {
                  method: 'POST', body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its files. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => { await fetchJSON(`/api/workflow/engreg/${request.id}`, { method: 'DELETE' }); navigate('/workflow/engreg'); }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

export default WorkflowEngRegDetail;
